import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import VariantDisplay from '../components/VariantDisplay';
import ProductOptionsDisplay from '../components/ProductOptionsDisplay';
import { BuyGuardDecisions, BuyGuardInfoTabs } from '../components/BuyGuardPreview';
import StarRating from '../components/StarRating';
import PriceHistoryPanel from '../components/PriceHistoryPanel';
import { getImageUrl } from '../utils/imageHelper';
import ListingImage from '../components/ListingImage';
import { 
  ArrowLeft, Edit, Trash2, Package, DollarSign, TrendingUp, 
  ChevronLeft, ChevronRight, ShoppingCart,
  Truck, Shield, RotateCcw, Tag, Store, MapPin, RefreshCw
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [product, setProduct] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [deleting, setDeleting] = React.useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [variantPriceAdjustment, setVariantPriceAdjustment] = useState(0);
  const [variantSelections, setVariantSelections] = useState({});
  const [masterVariantImage, setMasterVariantImage] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState('delivery'); // 'delivery' or 'pickup'
  const [resyncing, setResyncing] = useState(false);

  // iter-215 P1 — Re-sync to Shopify. Resets the 3-strike circuit breaker
  // and triggers an immediate retry of the nightly scheduler's push path.
  const handleResyncShopify = async () => {
    if (resyncing) return;
    setResyncing(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      // Reset the circuit breaker via the admin endpoint (works for the
      // owning vendor too — tightened at the route layer if needed later).
      const resetRes = await fetch(
        `${BACKEND_URL}/api/admin/scheduler/listings/${id}/reset-breaker`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!resetRes.ok && resetRes.status !== 403) {
        // Non-admin vendors will hit 403. Fall through to vendor-side resync below.
      }
      // Kick off a normal sync-to-shopify run for this listing.
      const pushRes = await fetch(
        `${BACKEND_URL}/api/shopify-sync/push/${id}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (pushRes.ok) {
        toast({ title: 'Shopify sync queued', description: 'Circuit breaker reset. Re-sync in progress.' });
        // Refresh product so the banner disappears if it succeeded.
        const refreshed = await fetch(`${BACKEND_URL}/api/listings/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (refreshed.ok) setProduct(await refreshed.json());
      } else {
        const err = await pushRes.text();
        toast({ title: 'Sync failed', description: err.slice(0, 200), variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Sync failed', description: String(e).slice(0, 200), variant: 'destructive' });
    } finally {
      setResyncing(false);
    }
  };
  
  React.useEffect(() => {
    const fetchProduct = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const token = sessionStorage.getItem('token');
        
        const response = await fetch(`${BACKEND_URL}/api/listings/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setProduct(data);
          
          // Set default delivery method based on logistics config
          const logistics = data.logistics || {};
          if (logistics.requires_shipping && !logistics.available_for_pickup) {
            setDeliveryMethod('delivery');
          } else if (!logistics.requires_shipping && logistics.available_for_pickup) {
            setDeliveryMethod('pickup');
          } else if (logistics.requires_shipping) {
            setDeliveryMethod('delivery'); // Default to delivery when both available
          }
        } else {
          navigate('/listings/products');
        }
      } catch (error) {
        console.error('Error:', error);
        navigate('/listings/products');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) fetchProduct();
  }, [id, navigate]);
  
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    setDeleting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');
      
      const response = await fetch(`${BACKEND_URL}/api/listings/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Product deleted successfully' });
        navigate('/listings/products');
      } else {
        toast({ title: 'Error', description: 'Failed to delete product', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error deleting product', variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  const handlePriceChange = (newPrice) => {
    setCalculatedPrice(newPrice);
  };

  // Handle variant selection changes
  const handleVariantSelectionChange = ({ selections, variantPrice: vPrice, priceAdjustment, hasSelection }) => {
    setVariantSelections(selections);
    // Use direct variant price if available (from combinations), otherwise calculate from adjustment
    if (vPrice !== null && vPrice !== undefined) {
      setVariantPriceAdjustment(vPrice - (parseFloat(data?.price) || 0));
    } else {
      setVariantPriceAdjustment(priceAdjustment || 0);
    }
  };

  // Handle master variant image change
  const handleMasterVariantImageChange = (imageUrl) => {
    if (imageUrl) {
      setMasterVariantImage(imageUrl);
      // Find index of this image in media array, or use as primary
      const imageIndex = product?.media?.indexOf(imageUrl);
      if (imageIndex !== -1) {
        setSelectedImage(imageIndex);
      }
    }
  };

  // Calculate the display price based on variant selection and custom options
  const getDisplayPrice = () => {
    // Base price from product data
    const basePrice = parseFloat(data?.price) || 0;
    
    // If custom options have calculated a price, use that as base
    // Then add variant price adjustments on top
    if (calculatedPrice !== null) {
      return calculatedPrice + variantPriceAdjustment;
    }
    
    // Otherwise use base price + variant adjustments
    return basePrice + variantPriceAdjustment;
  };

  // Get the current display image (master variant image takes priority)
  const getCurrentDisplayImage = () => {
    if (masterVariantImage) {
      return masterVariantImage;
    }
    return product?.media?.[selectedImage];
  };

  const nextImage = () => {
    if (product?.media?.length > 0) {
      setSelectedImage((prev) => (prev + 1) % product.media.length);
      setMasterVariantImage(null); // Reset master variant image when navigating
    }
  };

  const prevImage = () => {
    if (product?.media?.length > 0) {
      setSelectedImage((prev) => (prev - 1 + product.media.length) % product.media.length);
      setMasterVariantImage(null); // Reset master variant image when navigating
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }
  
  if (!product) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Product not found</div>
        </div>
      </DashboardLayout>
    );
  }
  
  const data = product.data || {};
  const images = product.media || [];
  const hasDiscount = data.comparePrice && data.comparePrice > data.price;
  const discountPercent = hasDiscount 
    ? Math.round(((data.comparePrice - data.price) / data.comparePrice) * 100) 
    : 0;
  
  // Calculate price range and total inventory from variant combinations
  // API returns variant_combinations (snake_case) after Pydantic conversion
  const variantCombinations = product.variant_combinations || product.variantCombinations || [];
  const hasVariants = variantCombinations.length > 0;
  
  const getPriceDisplay = () => {
    if (!hasVariants) {
      return `₹${data.price?.toLocaleString()}`;
    }
    
    // Get all variant prices
    const prices = variantCombinations.map(vc => vc.price).filter(p => p != null);
    if (prices.length === 0) {
      return `₹${data.price?.toLocaleString()}`;
    }
    
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    
    if (minPrice === maxPrice) {
      return `₹${minPrice.toLocaleString()}`;
    }
    
    return `₹${minPrice.toLocaleString()} - ₹${maxPrice.toLocaleString()}`;
  };
  
  const getTotalInventory = () => {
    if (!hasVariants) {
      return data.inventory || 0;
    }
    
    // Sum up all variant stock
    const totalStock = variantCombinations.reduce((sum, vc) => {
      const stock = vc.stock || vc.inventory || 0;
      return sum + stock;
    }, 0);
    
    return totalStock;
  };

  // Handle back navigation - go to previous page or fallback to all listings
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/listings/products');
    }
  };
  
  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header with Back and Actions */}
        <div className="flex items-center justify-between mb-6 px-4">
          <Button variant="ghost" onClick={handleBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/listings/products/edit/${id}`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              <Trash2 className="h-4 w-4 mr-2" />
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>

        {/* iter-215 P1 — per-listing sync warning banner + Re-sync button.
            Shows on ProductDetails when the scheduler flagged variant drops
            or tripped the 3-strike circuit breaker. */}
        {(product?.sync_warning || product?.sync_paused) && (
          <div
            className={`mx-4 mb-4 rounded-lg border-l-4 p-4 flex items-start justify-between gap-3 ${
              product?.sync_paused
                ? 'bg-red-50 border-red-500'
                : 'bg-amber-50 border-amber-500'
            }`}
            data-testid="product-sync-warning-banner"
          >
            <div className="flex-1 text-sm">
              <p className={`font-semibold ${product?.sync_paused ? 'text-red-900' : 'text-amber-900'}`}>
                {product?.sync_paused
                  ? 'Shopify sync paused'
                  : 'Shopify sync warning'}
              </p>
              <p className={`${product?.sync_paused ? 'text-red-800' : 'text-amber-800'} mt-0.5`}>
                {product?.sync_paused
                  ? (product.sync_paused_reason ||
                      `Circuit breaker tripped after ${product.sync_failure_count || 3} failed pushes. Click "Re-sync" to reset and retry.`)
                  : (product.sync_warning?.message ||
                      `${product.sync_warning?.dropped || 'Some'} variant(s) were dropped in the last scheduled push.`)}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              data-testid="product-sync-resync-btn"
              disabled={resyncing}
              onClick={handleResyncShopify}
              className={
                product?.sync_paused
                  ? 'text-red-700 border-red-300 hover:bg-red-100'
                  : 'text-amber-700 border-amber-300 hover:bg-amber-100'
              }
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${resyncing ? 'animate-spin' : ''}`} />
              {resyncing ? 'Re-syncing…' : 'Re-sync to Shopify'}
            </Button>
          </div>
        )}

        {/* Two Column Layout - Inspired by Storefront */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-4">
          
          {/* LEFT COLUMN - Product Gallery */}
          <div className="space-y-4">
            {/* Main Image with Navigation */}
            <div className="relative bg-white rounded-xl overflow-hidden border aspect-square">
              {(images.length > 0 || masterVariantImage) ? (
                <>
                  <ListingImage
                    src={masterVariantImage || images[selectedImage]}
                    alt={product.title}
                    className="w-full h-full object-contain"
                    showPlaceholder={false}
                  />
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  )}
                  {hasDiscount && (
                    <Badge className="absolute top-4 left-4 bg-red-500 text-white">
                      -{discountPercent}% OFF
                    </Badge>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <Package className="h-24 w-24 text-gray-300" />
                </div>
              )}
            </div>

            {/* Thumbnail Gallery */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((url, index) => (
                  <button
                    key={url.id || url.name || `${url.title || "url"}-${index}`}
                    onClick={() => {
                      setSelectedImage(index);
                      setMasterVariantImage(null); // Clear master variant image when manually selecting
                    }}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index && !masterVariantImage
                        ? 'border-blue-600 ring-2 ring-blue-200' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <ListingImage
                      src={url}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Tabs Section - Description, Specifications, etc (in left column) */}
            <div className="mt-8">
              <Tabs defaultValue="description" className="w-full">
                <TabsList className="w-full justify-start border-b bg-transparent h-auto p-0 gap-4 flex-wrap">
                  <TabsTrigger 
                    value="description" 
                    className="border-b-2 border-transparent data-[state=active]:border-gray-900 rounded-none pb-3 px-1 text-sm"
                  >
                    Description
                  </TabsTrigger>
                  <TabsTrigger 
                    value="specifications" 
                    className="border-b-2 border-transparent data-[state=active]:border-gray-900 rounded-none pb-3 px-1 text-sm"
                  >
                    Specifications
                  </TabsTrigger>
                  <TabsTrigger 
                    value="shipping" 
                    className="border-b-2 border-transparent data-[state=active]:border-gray-900 rounded-none pb-3 px-1 text-sm"
                  >
                    Shipping & Returns
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="description" className="pt-4">
                  <div className="prose max-w-none">
                    <p className="text-gray-600 whitespace-pre-wrap text-sm leading-relaxed">{product.description}</p>
                  </div>
                </TabsContent>

                <TabsContent value="specifications" className="pt-4">
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-700 mb-3 text-sm">Product Details</h4>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Category</dt>
                          <dd className="font-medium">{product.category}</dd>
                        </div>
                        {product.subcategory && (
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Subcategory</dt>
                            <dd className="font-medium">{product.subcategory}</dd>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Product Type</dt>
                          <dd className="font-medium capitalize">{data.productType || 'Physical'}</dd>
                        </div>
                        {data.sku && (
                          <div className="flex justify-between">
                            <dt className="text-gray-500">SKU</dt>
                            <dd className="font-medium">{data.sku}</dd>
                          </div>
                        )}
                      </dl>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-700 mb-3 text-sm">Pricing and Tax</h4>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-gray-500">{hasVariants ? 'Price Range' : 'Base Price'}</dt>
                          <dd className="font-medium">{getPriceDisplay()}</dd>
                        </div>
                        {data.comparePrice && (
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Compare at Price</dt>
                            <dd className="font-medium line-through text-gray-400">₹{data.comparePrice?.toLocaleString()}</dd>
                          </div>
                        )}
                        {data.chargeTax && (
                          <>
                            <div className="flex justify-between">
                              <dt className="text-gray-500">Tax Rate</dt>
                              <dd className="font-medium">{data.taxRate}%</dd>
                            </div>
                            {data.hsnCode && (
                              <div className="flex justify-between">
                                <dt className="text-gray-500">HSN/SAC Code</dt>
                                <dd className="font-medium">{data.hsnCode}</dd>
                              </div>
                            )}
                          </>
                        )}
                      </dl>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-700 mb-3 text-sm">Inventory</h4>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-gray-500">{hasVariants ? 'Total Available' : 'Available Quantity'}</dt>
                          <dd className="font-medium">{getTotalInventory()}</dd>
                        </div>
                        {hasVariants && (
                          <div className="flex justify-between">
                            <dt className="text-gray-500 text-xs">Across Variants</dt>
                            <dd className="font-medium text-xs text-gray-500">{variantCombinations.length} options</dd>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Track Inventory</dt>
                          <dd className="font-medium">{data.trackInventory ? 'Yes' : 'No'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Allow Backorders</dt>
                          <dd className="font-medium">{data.continueSellingOutOfStock ? 'Yes' : 'No'}</dd>
                        </div>
                      </dl>
                    </div>

                    {data.weight && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-700 mb-3 text-sm">Dimensions & Weight</h4>
                        <dl className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Weight</dt>
                            <dd className="font-medium">{data.weight} {data.weightUnit || 'kg'}</dd>
                          </div>
                          {data.dimensions && (
                            <div className="flex justify-between">
                              <dt className="text-gray-500">Dimensions (L × W × H)</dt>
                              <dd className="font-medium">{data.dimensions.length} × {data.dimensions.width} × {data.dimensions.height} cm</dd>
                            </div>
                          )}
                        </dl>
                      </div>
                    )}

                    {/* Tags */}
                    {data.tags && (
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2 text-sm">Tags</h4>
                        <div className="flex flex-wrap gap-2">
                          {(Array.isArray(data.tags) ? data.tags : (typeof data.tags === 'string' ? data.tags.split(',') : [])).map((tag, index) => (
                            <Badge key={tag.id || tag.name || `${tag.title || "tag"}-${index}`} variant="outline" className="text-xs">{typeof tag === 'string' ? tag.trim() : tag}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="shipping" className="pt-4">
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-700 mb-3 text-sm">Shipping Details</h4>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Serviceable Area</dt>
                          <dd className="font-medium capitalize">{data.serviceableType || 'Country'}</dd>
                        </div>
                        {data.serviceableType === 'pincodes' && data.serviceablePincodes && (
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Serviceable Pincodes</dt>
                            <dd className="font-medium">{data.serviceablePincodes}</dd>
                          </div>
                        )}
                        {data.weight && (
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Package Weight</dt>
                            <dd className="font-medium">{data.weight} {data.weightUnit || 'kg'}</dd>
                          </div>
                        )}
                      </dl>
                    </div>

                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-medium text-blue-800 mb-2 text-sm">Shipping Policy</h4>
                      <p className="text-xs text-blue-700">
                        Shipping costs and delivery times are calculated at checkout based on your location.
                        Free shipping may be available on orders above a certain value.
                      </p>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="font-medium text-green-800 mb-2 text-sm">Return Policy</h4>
                      <p className="text-xs text-green-700">
                        We offer hassle-free returns within 7-14 days of delivery.
                        Items must be in original condition with tags attached.
                      </p>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              {/* iter-215 P2 — Buy Guard Info Tabs (left column).
                  Mirrors the Shopify storefront's "What customers see"
                  tabs. Hides itself when the SSOT returns no grouped
                  decision fields. */}
              <BuyGuardInfoTabs listingId={id} />

              {/* iter-221i — Rolling 90-day price-change ledger. Auto-hides
                  when no changes have been recorded; vendors and admins
                  can both view their own listings' history here. */}
              <div className="mt-6">
                <PriceHistoryPanel listingId={id} />
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN - Product Info & Options */}
          <div className="space-y-6">
            {/* Product Title & Status */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={product.status === 'active' ? 'default' : product.status === 'draft' ? 'secondary' : 'outline'}>
                  {product.status === 'active' ? 'Active' : product.status === 'draft' ? 'Draft' : product.status}
                </Badge>
                <Badge variant="outline">{product.category}</Badge>
                {product.subcategory && (
                  <Badge variant="outline">{product.subcategory}</Badge>
                )}
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">{product.title}</h1>
              
              {/* Star Rating */}
              <div className="mt-2">
                <StarRating 
                  listingId={id}
                  size="md"
                  showCount={true}
                  fetchData={true}
                />
              </div>
            </div>

            {/* Pricing Section */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-baseline gap-3">
                <span className="text-3xl font-bold text-gray-900">
                  ₹{getDisplayPrice().toLocaleString()}
                </span>
                {hasDiscount && variantPriceAdjustment === 0 && !calculatedPrice && (
                  <span className="text-lg text-gray-500 line-through">
                    ₹{data.comparePrice?.toLocaleString()}
                  </span>
                )}
                {(variantPriceAdjustment > 0 || calculatedPrice !== null) && data.price && (
                  <span className="text-sm text-gray-500">
                    Base: ₹{(parseFloat(data.price) || 0).toLocaleString()}
                  </span>
                )}
              </div>
              {variantPriceAdjustment > 0 && (
                <p className="text-sm text-green-600 mt-1">
                  +₹{variantPriceAdjustment.toLocaleString()} for selected options
                </p>
              )}
              {/* Show shipping info based on logistics config */}
              {product.logistics?.requires_shipping || product.logistics?.available_for_pickup ? (
                <p className="text-sm text-gray-600 mt-1">
                  {deliveryMethod === 'pickup' ? '🏪 Store pickup selected - No shipping charges' : '🚚 Shipping calculated at checkout'}
                </p>
              ) : (
                <p className="text-sm text-gray-600 mt-1">Shipping calculated at checkout</p>
              )}
            </div>

            {/* Variants Display - shown first */}
            {(product.variants?.length > 0 || product.data?.variants?.length > 0) && (
              <VariantDisplay 
                variants={product.variants?.length > 0 ? product.variants : (product.data?.variants || [])} 
                variantCombinations={product.variant_combinations?.length > 0 ? product.variant_combinations : (product.data?.variant_combinations || product.data?.variantCombinations || [])}
                title="Select Options" 
                compact={true}
                basePrice={parseFloat(data.price) || 0}
                onSelectionChange={handleVariantSelectionChange}
                onImageChange={handleMasterVariantImageChange}
              />
            )}

            {/* Product Options (if available) */}
            {(product.has_custom_options || product.data?.hasCustomOptions || product.data?.customOptionsConfig?.options?.length > 0 || product.data?.productOptionsConfig?.options?.length > 0) && (
              <ProductOptionsDisplay 
                listingId={id}
                basePrice={parseFloat(data.price) || 0}
                variantSelections={variantSelections}
                variantPriceAdjustment={variantPriceAdjustment}
                onPriceChange={handlePriceChange}
              />
            )}

            {/* Delivery Method Selection */}
            {(product.logistics?.requires_shipping || product.logistics?.available_for_pickup) && (
              <div className="space-y-3">
                <h3 className="font-medium text-gray-900 text-sm">How would you like to receive this?</h3>
                <div className="grid grid-cols-2 gap-3">
                  {/* Delivery Option */}
                  {product.logistics?.requires_shipping && (
                    <button
                      onClick={() => setDeliveryMethod('delivery')}
                      className={`flex flex-col items-center p-4 rounded-lg border-2 transition-all ${
                        deliveryMethod === 'delivery'
                          ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-200'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Truck className={`h-6 w-6 mb-2 ${deliveryMethod === 'delivery' ? 'text-blue-600' : 'text-gray-500'}`} />
                      <span className={`font-medium text-sm ${deliveryMethod === 'delivery' ? 'text-blue-700' : 'text-gray-700'}`}>
                        🚚 Delivery
                      </span>
                      <span className="text-xs text-gray-500 mt-1">Ship to your address</span>
                      {deliveryMethod === 'delivery' && (
                        <Badge className="mt-2 bg-blue-600 text-white text-xs">Selected</Badge>
                      )}
                    </button>
                  )}
                  
                  {/* Store Pickup Option */}
                  {product.logistics?.available_for_pickup && (
                    <button
                      onClick={() => setDeliveryMethod('pickup')}
                      className={`flex flex-col items-center p-4 rounded-lg border-2 transition-all ${
                        deliveryMethod === 'pickup'
                          ? 'border-green-600 bg-green-50 ring-2 ring-green-200'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Store className={`h-6 w-6 mb-2 ${deliveryMethod === 'pickup' ? 'text-green-600' : 'text-gray-500'}`} />
                      <span className={`font-medium text-sm ${deliveryMethod === 'pickup' ? 'text-green-700' : 'text-gray-700'}`}>
                        🏪 Store Pickup
                      </span>
                      <span className="text-xs text-gray-500 mt-1">Pick up from store</span>
                      {deliveryMethod === 'pickup' && (
                        <Badge className="mt-2 bg-green-600 text-white text-xs">Selected</Badge>
                      )}
                    </button>
                  )}
                </div>
                
                {/* Delivery Info Badge */}
                <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                  deliveryMethod === 'delivery' 
                    ? 'bg-blue-50 text-blue-700' 
                    : 'bg-green-50 text-green-700'
                }`}>
                  {deliveryMethod === 'delivery' ? (
                    <>
                      <Truck className="h-4 w-4" />
                      <span>Shipping charges will be calculated at checkout</span>
                    </>
                  ) : (
                    <>
                      <MapPin className="h-4 w-4" />
                      <span>Free pickup • No shipping charges</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* iter-215 P2 — Buy Guard Decision Fields (right column).
                Shows exactly what customers pick on Shopify: locality
                picker, day chips, pincode check, Tier-2 notice, etc.
                Fed by /api/serviceability/listing/{id}/checkout-config. */}
            <BuyGuardDecisions listingId={id} />

            {/* Add to Cart & Buy Now (Preview) */}
            <div className="space-y-3">
              <Button className="w-full h-12 text-lg bg-gray-900 hover:bg-gray-800" disabled>
                <ShoppingCart className="h-5 w-5 mr-2" />
                ADD TO CART
              </Button>
              <Button variant="outline" className="w-full h-12 text-lg" disabled>
                BUY IT NOW
              </Button>
              <p className="text-xs text-center text-gray-500">
                (Preview mode - Buttons disabled in admin view)
              </p>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-4 border-t">
              <div className="text-center">
                <Truck className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Fast Shipping</p>
              </div>
              <div className="text-center">
                <Shield className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Secure Payment</p>
              </div>
              <div className="text-center">
                <RotateCcw className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Easy Returns</p>
              </div>
            </div>

            {/* Quick Info */}
            <div className="bg-blue-50 rounded-lg p-4 space-y-2">
              {isAdmin && product.vendor_name && (
                <div className="flex items-center gap-2 text-sm border-b border-blue-100 pb-2 mb-2">
                  <Store className="h-4 w-4 text-blue-600" />
                  <span className="text-gray-500">Seller:</span>
                  <span className="font-medium text-gray-900">{product.vendor_name}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm">
                <Tag className="h-4 w-4 text-blue-600" />
                <span className="text-gray-700">SKU: {data.sku || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Package className="h-4 w-4 text-blue-600" />
                <span className="text-gray-700">
                  {data.trackInventory 
                    ? `${getTotalInventory()} in stock${hasVariants ? ' (across all variants)' : ''}` 
                    : 'Stock not tracked'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <TrendingUp className="h-4 w-4 text-blue-600" />
                <span className="text-gray-700 capitalize">{data.productType || 'Physical'} Product</span>
              </div>
            </div>
          </div>
        </div>

        {/* SEO Section (Admin Only) */}
        {(data.seoTitle || data.seoDescription) && (
          <div className="mt-8 px-4">
            <Card className="border-dashed">
              <CardHeader>
                <CardTitle className="text-sm text-gray-500">SEO Preview (Admin Only)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.seoTitle && (
                  <div>
                    <p className="text-blue-600 text-lg hover:underline cursor-pointer">{data.seoTitle}</p>
                  </div>
                )}
                {data.seoDescription && (
                  <p className="text-sm text-gray-600">{data.seoDescription}</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProductDetails;